import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, BookOpen, ExternalLink } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";

const EMPTY = {
  name: "", publisher: "", quartile: "Q2", domains: [], apc_usd: 0,
  issn: "", sjr: null, h_index: null, url: "", open_access: false, indexing: [],
};

export default function AdminJournalsPage() {
  const [items, setItems] = useState([]);
  const pager = usePager(items, 20);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/journals", { params: search ? { q: search } : {} });
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search]);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (j) => {
    setEditing(j.id);
    setForm({ ...EMPTY, ...j, domains: j.domains || [], indexing: j.indexing || [] });
  };
  const save = async () => {
    if (!form.name.trim() || !form.publisher.trim()) {
      toast.error("Name and publisher required"); return;
    }
    try {
      if (editing === "new") await api.post("/admin/journals", form);
      else await api.put(`/admin/journals/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this journal? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/journals/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-journals-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">JOURNAL DATABASE</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Curate <em className="italic text-kb-primary">target journals</em></h1>
        </div>
        <button onClick={startNew} className="kb-btn-primary text-sm" data-testid="journal-new-btn">
          <Plus className="w-3.5 h-3.5" /> New journal
        </button>
      </div>

      <div className="mt-6">
        <input className="kb-input text-sm max-w-md" placeholder="Search by name, publisher or domain…"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="journal-search-input" />
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="journal-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW JOURNAL" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="kb-input text-sm sm:col-span-2" placeholder="Journal name *"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="journal-name-input" />
            <input className="kb-input text-sm" placeholder="Publisher *"
              value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })}
              data-testid="journal-publisher-input" />
            <select className="kb-input text-sm" value={form.quartile}
              onChange={(e) => setForm({ ...form, quartile: e.target.value })} data-testid="journal-quartile-select">
              {["Q1", "Q2", "Q3", "Q4"].map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
            <input className="kb-input text-sm sm:col-span-2" placeholder="Domains (comma-separated)"
              value={(form.domains || []).join(", ")}
              onChange={(e) => setForm({ ...form, domains: e.target.value.split(",").map((d) => d.trim()).filter(Boolean) })} />
            <input type="number" min="0" className="kb-input text-sm" placeholder="APC (USD)"
              value={form.apc_usd || 0} onChange={(e) => setForm({ ...form, apc_usd: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 0)) })} />
            <input className="kb-input text-sm" placeholder="ISSN" value={form.issn || ""}
              onChange={(e) => setForm({ ...form, issn: e.target.value })} />
            <input type="number" step="0.01" className="kb-input text-sm" placeholder="SJR" value={form.sjr ?? ""}
              onChange={(e) => setForm({ ...form, sjr: e.target.value === "" ? null : parseFloat(e.target.value) })} />
            <input type="number" min="0" className="kb-input text-sm" placeholder="h-index" value={form.h_index ?? ""}
              onChange={(e) => setForm({ ...form, h_index: e.target.value === "" ? null : parseInt(e.target.value) })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Homepage URL"
              value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Indexing (e.g. Scopus, WoS, PubMed)"
              value={(form.indexing || []).join(", ")}
              onChange={(e) => setForm({ ...form, indexing: e.target.value.split(",").map((d) => d.trim()).filter(Boolean) })} />
            <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
              <input type="checkbox" checked={!!form.open_access}
                onChange={(e) => setForm({ ...form, open_access: e.target.checked })} />
              Open access
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="journal-save-btn">Save journal</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
            No journals match your search.
          </div>
        ) :
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {pager.pageItems.map((j) => (
            <div key={j.id} className="kb-card p-5" data-testid={`admin-journal-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <BookOpen className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                  <div className="font-serif text-lg text-kb-ink mt-2 leading-tight">{j.name}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                    {j.publisher} · <span className="text-kb-primary">{j.quartile}</span>
                    {j.apc_usd ? ` · APC $${Number(j.apc_usd).toLocaleString()}` : " · No APC"}
                  </div>
                  {j.domains?.length > 0 && (
                    <div className="text-xs text-kb-ink-muted mt-2 line-clamp-1">{j.domains.join(" · ")}</div>
                  )}
                  {j.indexing?.length > 0 && (
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-2">
                      {j.indexing.join(" · ")}
                    </div>
                  )}
                  {j.url && (
                    <a href={j.url} target="_blank" rel="noreferrer"
                      className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1 mt-2">
                      Homepage <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => startEdit(j)} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`admin-journal-${j.id}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(j.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`admin-journal-${j.id}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>}
      <Pager {...pager} label="journals" testId="admin-journals-pager" />
    </div>
  );
}